/**
 * `heysegment switch` — rebind the session to the workspace's working version.
 *
 * When the bound version has been published + made live (frozen) or otherwise
 * superseded, the editable "working version" is a different version id. This
 * authorizes with the current session, asks the server to mint a fresh session
 * bound to the working version (forking a draft from live if needed), saves the
 * new identity over `.heysegment/`, and pulls the new version's source.
 *
 * Auth is unchanged: the new session is a normal mint (same token scheme, 7-day
 * TTL); only the locally-saved `session.json` + credential are rewritten.
 *
 * @module
 */
import { Console, Effect } from "effect"
import { Command } from "effect/unstable/cli"
import { withSession, step } from "../session/with-session"
import { downloadAndUnpack } from "../session/pull-core"
import { writeCredential, writeSessionFile } from "../session/metadata"
import { SessionClient, sessionLayer } from "../rpc"
import { apiUrlFlag, dirFlag } from "./flags"

export const switchCommand = Command.make(
  "switch",
  { dir: dirFlag, apiUrl: apiUrlFlag },
  (flags) =>
    withSession({ dir: flags.dir, apiUrl: flags.apiUrl }, (session) =>
      Effect.gen(function* () {
        const client = yield* SessionClient
        const result = yield* step("switch:rebind", client.adoptWorkingVersion(void 0))

        if (result.alreadyCurrent) {
          yield* Console.log(
            `Already on the current working version (v${result.versionNumber}). Nothing to switch.`,
          )
          return
        }

        // Persist the new identity. workspaceId + apiBaseUrl are unchanged, so we
        // reconstruct session.json from the rebind result + what we already had.
        const now = new Date().toISOString()
        yield* writeSessionFile(session.projectDir, {
          schemaVersion: 1,
          sessionId: result.sessionId,
          workspaceId: session.workspaceId,
          versionId: result.versionId,
          apiBaseUrl: session.baseUrl,
          createdAt: now,
        })
        yield* writeCredential({
          baseUrl: session.baseUrl,
          sessionId: result.sessionId,
          token: result.sessionToken,
          savedAt: now,
        })

        yield* Console.log(
          result.createdNew
            ? `✓ Now on v${result.versionNumber} (forked from live)`
            : `✓ Now on v${result.versionNumber}`,
        )

        // Pull the new version's bundle, authenticated as the just-minted session.
        yield* downloadAndUnpack({ projectDir: session.projectDir }).pipe(
          Effect.provide(
            sessionLayer(session.baseUrl, {
              sessionId: result.sessionId,
              token: result.sessionToken,
            }),
          ),
        )
        yield* Console.log(`✓ Pulled v${result.versionNumber} into ${session.projectDir}`)
      }),
    ),
).pipe(
  Command.withDescription(
    "Switch the session to the workspace's current working version and pull it",
  ),
)
